import React from "react";
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  IconButton,
  Link
} from "@mui/material";


import {
  Gavel,
  Info,
  ReportProblem,
  Person,
  Assignment,
  Payment,
  LocalOffer,
  Forum,
  VerifiedUser,
  Copyright,
  Warning,
  Security,
  Description,
  Public,
  Business,
  AssignmentTurnedIn,
  Phone,
  Balance,
  Policy
} from '@mui/icons-material';
import NavBar from "./landingpage/navbar";
import Footer from "./landingpage/footer";
export const PrivacyPolicy = () => {
  return (
    <>
      <NavBar />
      <Container maxWidth="md" sx={{ my: 4, border: '2px solid #000', p: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center" gutterBottom>
          Last Updated: April 23, 2025
        </Typography>
        
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Introduction
            </Typography>
            <Typography>
              Welcome to Anypaperpro.com (the "Website"), a service provided by S.K.M. Educational Media Ltd and its subsidiaries ("S.K.M. Educational Media", "we", "us", or "our").
            </Typography>
            <Typography>
              For the purposes of General Data Protection Regulation (EU) 2016/679 (GDPR), S.K.M. Educational Media Ltd, a Cyprus company, having its registered address at Piliou 11-13, office 201, 6037, Larnaca, Cyprus, and Registration No. HE 470803 is the "controller" of your personal data, meaning we determine the purposes and means of handling your personal data.
            </Typography>
            <Typography>
              Your privacy is important to us, and we take it very seriously. Therefore, we encourage you to carefully read this Privacy Policy, which explains important information regarding, among others:
            </Typography>
            <List>
              <ListItem>About Your Consent</ListItem>
              <ListItem>What Information We Collect About You</ListItem>
              <ListItem>How Long We Keep Your Information</ListItem>
              <ListItem>How We Share Your Information, including Categories of Recipients of Personal Data</ListItem>
              <ListItem>Where the Personal Data of Yours Can Be Processed</ListItem>
              <ListItem>About Cookies Files</ListItem>
              <ListItem>About Marketing and Opting Out</ListItem>
              <ListItem>About Your Rights</ListItem>
              <ListItem>Whom You Can Complain To</ListItem>
              <ListItem>About Children</ListItem>
              <ListItem>How to Contact Us</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Why We Collect and Process Your Personal Data
            </Typography>
            <Typography>
              We are committed to protecting your privacy and only collect your personal data for legal purposes and in compliance with applicable personal data protection laws. We process your personal data for purposes such as:
            </Typography>
            <List>
              <ListItem>To provide the Services you request</ListItem>
              <ListItem>To ensure that the Website content is presented in the most effective manner for your device</ListItem>
              <ListItem>To contact you regarding an order or any related process</ListItem>
              <ListItem>To carry out our obligations arising from any contracts entered into between you and S.K.M. Educational Media</ListItem>
              <ListItem>To manage your account, including processing payments and refunds, and providing notifications</ListItem>
              <ListItem>To keep our Website secure and functioning</ListItem>
              <ListItem>To notify you about changes to our Services</ListItem>
              <ListItem>To respond to and address any comments, queries, or complaints you raise regarding the Website and our Services</ListItem>
              <ListItem>To conduct research, statistical, and behavioral analysis</ListItem>
              <ListItem>To contact you for marketing purposes, where applicable</ListItem>
              <ListItem>To assist with managing our website, conducting diagnostics, and handling data testing, analysis, or surveys</ListItem>
              <ListItem>To make suggestions and recommendations about services that may interest you</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Legal Basis for Handling Your Information
            </Typography>
            <Typography>
              We can only process your personal data when we have a valid legal basis. For the purposes of this Privacy Policy, the legal basis for processing your personal data is:
            </Typography>
            <List>
              <ListItem>Your consent, where necessary for specific processing activities (e.g., direct marketing)</ListItem>
              <ListItem>The necessity of processing for the performance of a contract between you and S.K.M. Educational Media or to take pre-contractual steps at your request</ListItem>
              <ListItem>Our legitimate interest in protecting our Website, preventing fraud, securing your account, providing quality customer support, and complying with legal obligations</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your Consent
            </Typography>
            <Typography>
              Before we process your personal data for specific purposes, we will seek your consent, especially for activities like direct marketing. If you have previously given consent, you may withdraw it at any time by contacting us at management@anypaperpro.com. If you withdraw your consent and we do not have another legal basis to process your data, we will stop processing it. However, please note that if you withdraw consent, you may not be able to access certain services on the Website.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Information We Collect About You
            </Typography>
            <Typography>
              We collect the following types of personal data:
            </Typography>
            <List>
              <ListItem>Identification data such as your name, email address, and phone number</ListItem>
              <ListItem>Transaction data, including purchases, payments, and other details related to your use of the Website</ListItem>
              <ListItem>Account-related information, including your username, password, and account settings</ListItem>
              <ListItem>Technical data, such as your IP address, browser type, and device information</ListItem>
              <ListItem>Usage data, including information about how you interact with the Website and its features</ListItem>
              <ListItem>Marketing and communications data, including your preferences in receiving marketing communications from us</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sharing Your Information
            </Typography>
            <Typography>
              We may share your personal data with the following categories of recipients:
            </Typography>
            <List>
              <ListItem>Service providers who assist us in running our Website and delivering Services to you</ListItem>
              <ListItem>Legal authorities, when required by law or in response to a valid legal request</ListItem>
              <ListItem>Business partners or affiliates, for purposes of business operations and marketing</ListItem>
              <ListItem>Any third parties involved in a business transaction, such as a merger or sale</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Data Retention
            </Typography>
            <Typography>
              We retain your personal data only as long as necessary to fulfill the purposes outlined in this Privacy Policy or as required by law.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cookies and Tracking Technologies
            </Typography>
            <Typography>
              We use cookies and similar tracking technologies to enhance your user experience and collect information about your activity on the Website. You can manage your cookie preferences through your browser settings.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your Rights
            </Typography>
            <Typography>
              You have the right to:
            </Typography>
            <List>
              <ListItem>Request access to your personal data</ListItem>
              <ListItem>Request correction or deletion of your personal data</ListItem>
              <ListItem>Request restriction of processing of your personal data</ListItem>
              <ListItem>Object to the processing of your personal data</ListItem>
              <ListItem>Request data portability</ListItem>
              <ListItem>Withdraw your consent, where applicable</ListItem>
            </List>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Complaints
            </Typography>
            <Typography>
              If you believe that your rights have been violated, you can lodge a complaint with the relevant data protection authority. You can also contact us directly at management@anypaperpro.com.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Children’s Privacy
            </Typography>
            <Typography>
              The Website is not intended for children under the age of 13, and we do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child under the age of 13, we will take steps to delete such information.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Changes to this Privacy Policy
            </Typography>
            <Typography>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page, and the date of the latest update will be indicated at the top of the page.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact Us
            </Typography>
            <Typography>
              If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at:
            </Typography>
            <Typography color="primary" sx={{ mt: 1 }}>
              Email: <a href="mailto:management@anypaperpro.com">management@anypaperpro.com</a>
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
};


export const RefundPolicy = () => {
  return (
    <>
      <NavBar />
      <Container maxWidth="md" sx={{ my: 4, border: '2px solid #000', p: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Refund Policy
        </Typography>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              General Terms
            </Typography>
            <Typography paragraph>
              Refund requests can be made before the delivered paper is approved by you. Once approval is given, no refund will be processed unless specified otherwise in this policy. The refund amounts are determined based on the stage of the order, the work completed, and the time elapsed.
            </Typography>

            <Typography variant="subtitle1">1. 100% Refund</Typography>
            <List>
              <ListItem><ListItemText primary="Payment Mistakes: If a double payment or identical order is placed, a refund will be issued if no writer has been assigned." /></ListItem>
              <ListItem><ListItemText primary="No Suitable Writer Available: A full refund will be issued if we are unable to assign a suitable writer." /></ListItem>
              <ListItem><ListItemText primary="Deadline Missed: If the paper is not delivered on time and no content is provided, a full refund is granted. No rights to the material are retained." /></ListItem>
            </List>

            <Typography variant="subtitle1">2. 70% Refund</Typography>
            <List>
              <ListItem><ListItemText primary="If the order is canceled after a writer is assigned and less than 50% of the deadline has passed." /></ListItem>
            </List>

            <Typography variant="subtitle1">3. 50% Refund</Typography>
            <List>
              <ListItem><ListItemText primary="If a revision is required but no writer is available to complete it." /></ListItem>
              <ListItem><ListItemText primary="If the order is canceled after a writer is assigned and more than 50% of the deadline has passed." /></ListItem>
            </List>

            <Typography variant="h6" gutterBottom>Approval and Revision Conditions</Typography>
            <Typography paragraph>
              Once you approve the paper or send it for revision, it is assumed you are satisfied with the work. Refunds are not possible after this unless the work is demonstrably flawed or deviates significantly from initial instructions. If a revision is delayed, our Customer Experience Manager may grant a refund of up to 50% depending on the situation.
            </Typography>
            <Typography paragraph>
              If the revision delay is beyond your control, a refund may be issued but will not exceed 50% of the order price.
            </Typography>

            <Typography variant="h6" gutterBottom>Copyright and Material Use</Typography>
            <Typography paragraph>
              If a refund is approved, all rights granted under our Copyright License cease immediately. You may not use or distribute any material associated with the refunded order.
            </Typography>

            <Typography variant="h6" gutterBottom>How to Request a Refund</Typography>
            <Typography paragraph>
              Contact our customer support via email within 14 days of receiving the final work. Requests must include detailed explanations or evidence such as plagiarism reports or proof of late delivery. Refunds are processed only under the conditions outlined in this policy.
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
};





export const TermsAndPrivacy = () => {
  return (
    <>
      <NavBar />
      <Container maxWidth="md" sx={{ my: 4, border: '2px solid #000', p: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Terms and Conditions
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Last Updated and Effective: April 16th, 2025
        </Typography>
        <Typography paragraph sx={{ mb: 4 }}>
          Please read these Terms and Conditions ("Terms") carefully before using the Anypaperpro.com website (the "Website") operated by S.K.M. Educational Media Ltd.
        </Typography>

        {/* Section 1: Definitions */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">1. Definitions</Typography>
            </Box>
            <Typography paragraph>
              In these Terms, the words "you" and "your" refer to you, the end user accessing the Website, whether as an individual or on behalf of an institution or company. The words "we," "us," "our," and "Anypaperpro.com" refer to S.K.M. Educational Media Ltd, a Cyprus-based company registered under number HE 470803, with its registered address at Pilliou 11-13, Office 201, 6037, Larnaca, Cyprus.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 2: Agreement to Terms */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">2. Agreement to Terms</Typography>
            </Box>
            <Typography paragraph>
              By accessing and using the Website, you agree to be legally bound by these Terms and Conditions. If you do not agree with any part of these Terms, you must not use the Website. Your use of the Website also signifies agreement to any policies referenced herein, including but not limited to the Refund Policy and Revision Policy, as well as any additional terms posted on the Website.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 3: Eligibility */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">3. Eligibility</Typography>
            </Box>
            <Typography paragraph>
              By using this Website, you confirm that you are at least 18 years of age and have full legal capacity to enter into binding agreements. You also affirm that the information you submit is accurate and up-to-date, and that your use of the Website and its services does not violate any laws, regulations, or educational institution policies applicable to you. You further agree that it is your responsibility to submit your own work to your educational institution and that any use of materials provided by the Website must be properly cited.
            </Typography>
            <Typography paragraph>
              We reserve the right to terminate your access to the Services without notice if we believe you do not meet the eligibility criteria or have provided false information.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 4: Scope of Services */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">4. Scope of Services</Typography>
            </Box>
            <Typography paragraph>
              Subject to full compliance with these Terms, we provide academic writing services for personal and research use. These services include the creation of original papers such as essays, dissertations, term papers, research papers, and reports. We may also offer editing, proofreading, paraphrasing, or formatting of documents you provide.
            </Typography>
            <Typography paragraph>
              All written materials are intended solely for reference purposes. It is your responsibility to ensure that the materials are used lawfully and appropriately. We do not condone plagiarism or academic dishonesty and will not be held responsible for your misuse of the services.
            </Typography>
            <Typography paragraph>
              Unless otherwise stated by you at the time of placing an order, we reserve the right to use any suitable sources for completing the assignment, including books, articles, journals, websites, interviews, and publicly available data.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 5: Order Placement */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">5. Order Placement</Typography>
            </Box>
            <Typography paragraph>
              Orders for Services must be submitted via the official order form on the Website. It is your responsibility to ensure that all order details and personal information provided are complete, accurate, and up-to-date. Failure to provide accurate information may result in delays or complications for which we will not be held liable.
            </Typography>
            <Typography paragraph>
              We will communicate with you regarding your order through the contact information you provide. Communication may include email, phone, or SMS notifications. It is your responsibility to ensure these communication channels are valid and operational.
            </Typography>
            <Typography paragraph>
              Once you confirm your order details, they cannot be changed without approval, and any significant modifications may be treated as a new order requiring additional payment. We do not begin working on orders until full payment has been received and order instructions are confirmed.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 6: Payment Terms */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">6. Payment Terms</Typography>
            </Box>
            <Typography paragraph>
              All payments must be made in advance. If your payment fails or is rejected by your payment provider, your order will not be processed. We are not responsible for any issues caused by declined payments or delayed processing.
            </Typography>
            <Typography paragraph>
              The price of your order is calculated based on the academic level, type of service, order deadline, and any additional options selected. Pricing may be subject to change without prior notice, and we reserve the right to update our rates at any time.
            </Typography>
            <Typography paragraph>
              If a chargeback is initiated, you automatically forfeit all rights to use the completed work. In such cases, we reserve the right to pursue copyright infringement claims, to publish the completed work, and to initiate authorship investigations to determine whether it was used in violation of these Terms.
            </Typography>
            <Typography paragraph>
              By submitting your payment information, you confirm that you are authorized to use the payment method and that you consent to us using third-party payment processors to complete the transaction securely.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 7: Discount Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">7. Discount Policy</Typography>
            </Box>
            <Typography paragraph>
              We may, at our sole discretion, offer promotional pricing, coupons, or discounts. These discounts apply only when explicitly offered and must be applied at the time of order. We do not apply retroactive discounts once payment has been completed, so it is your responsibility to review the final price before proceeding with payment.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 8: Refund Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">8. Refund Policy</Typography>
            </Box>
            <Typography paragraph>
              Refunds are available only under specific conditions, as described in our separate Refund Policy (Money Back Guarantee). Refunds will only be issued to the original payment method. Claims for refunds must follow the process outlined on our Website.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 9: Revision Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">9. Revision Policy</Typography>
            </Box>
            <Typography paragraph>
              We are committed to your satisfaction and offer up to three free revisions, provided the request aligns with your original order instructions. Revision requests must not contradict the original instructions and must be submitted within seven days of order approval, or within fourteen days for orders exceeding 20 pages.
            </Typography>
            <Typography paragraph>
              You may request a revision via the "Send for Revision" button on your order page or by contacting our support team. If your three free revisions are exhausted or the new instructions deviate from the original order, you will be required to place a new order for editing or rewriting.
            </Typography>
            <Typography paragraph>
              Once a revised document is delivered, you have 14 days to approve it. After this period, the revision will be automatically approved.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 10: Dispute Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">10. Dispute Policy</Typography>
            </Box>
            <Typography paragraph>
              A dispute may arise if a client contacts their bank to initiate a chargeback. Once notified of a dispute, our support team will attempt to contact you to resolve the issue. We may request additional documentation and submit evidence to the relevant financial institution confirming that the services were rendered properly and on time.
            </Typography>
            <Typography paragraph>
              All information related to disputes will remain confidential. We are not liable for any consequential or indirect damages resulting from a dispute.
            </Typography>
            <Typography paragraph>
              We reserve the right to amend this policy at any time. The most recent version will be published on the Website.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 11: Communication Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">11. Communication Policy</Typography>
            </Box>
            <Typography paragraph>
              By using our services, you agree to receive communication via the contact methods you provide. We may contact you with updates regarding your order, as well as promotional content, unless you opt out by informing our support team.
            </Typography>
            <Typography paragraph>
              All communication will be considered valid and binding if sent to the contact details you submitted when placing your order. It is your responsibility to ensure you can receive communications from our domain.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 12: Warranties and Representations */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">12. Warranties and Representations</Typography>
            </Box>
            <Typography paragraph>
              By agreeing to these Terms, you confirm that all documents received from the Website will be used solely for research, reference, or sample purposes. You may not present any portion of the work as your own unless properly cited.
            </Typography>
            <Typography paragraph>
              You acknowledge that all Papers are created by freelance writers who transfer their rights to Anypaperpro.com upon completion. You also acknowledge that these materials are provided as samples to demonstrate proper formatting, style, and structure.
            </Typography>
            <Typography paragraph>
              You agree not to copy, sell, distribute, publish, or submit the provided materials without prior written permission and proper attribution. We do not guarantee specific academic outcomes or grades, and we do not support any violation of academic integrity policies.
            </Typography>
            <Typography paragraph>
              We warrant that all Papers are original, properly researched, and free from plagiarism exceeding 15%, excluding properly cited references and commonly used phrases.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 13: Intellectual Property Rights */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">13. Intellectual Property Rights</Typography>
            </Box>
            <Typography paragraph>
              All content published on the Website, including text, source code, design, software, and multimedia elements, is protected by copyright and remains the sole property of Anypaperpro.com or its licensors. Unauthorized use or reproduction of Website content is strictly prohibited.
            </Typography>
            <Typography paragraph>
              All Papers delivered to clients are the intellectual property of Anypaperpro.com. You are granted a limited, non-exclusive, non-transferable license to use the Paper for personal research purposes only. You may not modify, reproduce, publish, or distribute the Paper without our written consent. You may not insert your name into the document or submit it as your own work.
            </Typography>
            <Typography paragraph>
              Any violation of these restrictions will result in the immediate termination of your rights to use the materials and may result in legal action.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 14: Limitation of Liability */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">14. Limitation of Liability</Typography>
            </Box>
            <Typography paragraph>
              We shall not be held liable for your academic performance, grades, or consequences arising from submission of our materials to any academic institution. We also disclaim responsibility for technical issues, data loss, or any indirect or incidental damages related to your use of the Website.
            </Typography>
            <Typography paragraph>
              You assume full responsibility for how you use the materials and for ensuring their proper citation and ethical application.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 15: Indemnification */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">15. Indemnification</Typography>
            </Box>
            <Typography paragraph>
              You agree to indemnify and hold harmless Anypaperpro.com, its affiliates, officers, employees, agents, and third-party service providers from any claims, damages, liabilities, legal fees, or losses resulting from your use of our Services, your violation of these Terms, or your infringement of any third-party rights.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 16: Prohibited Jurisdictions */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">16. Prohibited Jurisdictions</Typography>
            </Box>
            <Typography paragraph>
              Our services are not available to residents of the United Kingdom, Australia, Austria, or the following U.S. states: California, Colorado, Connecticut, Florida, Illinois, Maine, Maryland, Massachusetts, Nevada, New Jersey, New York, North Carolina, Oregon, Pennsylvania, Texas, Virginia, and Washington. If you are located in any of these jurisdictions, you are prohibited from using the Website, and your account may be terminated without notice.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 17: Final Provisions */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">17. Final Provisions</Typography>
            </Box>
            <Typography paragraph>
              A complete understanding is shaped by this document between you and us. Verbal or written information is not to be trusted unless it specifically refers to this document.
            </Typography>
            <Typography paragraph>
              If you or we are not able to impose the execution of any part of these Terms and Conditions, it does not relinquish the rights to implement any other part of this document in the future.
            </Typography>
            <Typography paragraph>
              This document and no part of it creates any kind of joint venture, employee-employer relationship and/or partnership between you and us. It also does not authorize either you or us to act as a representative or agent of each other.
            </Typography>
            <Typography paragraph>
              Third parties do not get any rights resulting from these Terms. Moreover, third-party obligations are not to be created based on these Terms.
            </Typography>
            <Typography paragraph>
              The English language is the only one used for the communication between us and you as well as most transactions conducted through this Website.
            </Typography>
            <Typography paragraph>
              These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of Cyprus. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Larnaca, Cyprus.
            </Typography>
            <Typography paragraph>
              We reserve the right to amend, update, or modify these Terms and Conditions at any time, without prior notice. Any changes will become effective immediately upon being posted on the Website. Your continued use of the Website after such changes have been posted constitutes your acceptance of the revised Terms and Conditions.
            </Typography>
            <Typography paragraph>
              Date of latest update: April 16th, 2025.
            </Typography>
            <Typography paragraph>
              The electronic form of this Terms and Conditions shall have equal legal force as if signed in ink.
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
};


export const CookiePolicy = () => {
  return (
    <>
      <NavBar />
      <Container maxWidth="md" sx={{ my: 4, border: '2px solid #000', p: 3 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Cookie Policy
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Last updated: April 23, 2025
        </Typography>
        <Typography paragraph sx={{ mb: 4 }}>
          This Cookie Policy explains how Anypaperpro.com ("we", "us", or "our") uses cookies and similar technologies to recognize you when you visit our website. It also explains what these technologies are, why we use them, and your rights to control their use.
        </Typography>
        <Typography paragraph sx={{ mb: 4 }}>
          By continuing to use our website, you consent to our use of cookies and similar tracking technologies as described in this Cookie Policy.
        </Typography>

        {/* Section 1: What Are Cookies? */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">What Are Cookies?</Typography>
            </Box>
            <Typography paragraph>
              Cookies are small text files placed on your device when you visit a website. They are commonly used to ensure websites function properly or more efficiently, as well as to provide information to the site owners. Cookies may also be used to collect data related to your browsing activities.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 2: Types of Cookies We Use */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">Types of Cookies We Use</Typography>
            </Box>
            <Typography paragraph>
              We use both first-party and third-party cookies for several purposes. Some cookies are strictly necessary for technical reasons, while others enable us to improve your browsing experience or gather analytical insights.
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 1.5 }}>
              <Typography component="li" paragraph>
                <strong>Strictly Necessary Cookies</strong> - These cookies are essential for the functioning of our website and cannot be switched off in our systems. They are usually set in response to actions you take, such as setting privacy preferences, logging in, or filling out forms.
              </Typography>
              <Typography component="li" paragraph>
                <strong>Performance and Analytics Cookies</strong> - These cookies collect information about how visitors interact with our website. We use this data to improve website functionality and understand user behavior on our pages.
              </Typography>
              <Typography component="li" paragraph>
                <strong>Functionality Cookies</strong> - These cookies enable the website to provide enhanced functionality and personalization, such as remembering your preferences and previously viewed pages. If you do not allow these cookies, some or all of these services may not function properly.
              </Typography>
              <Typography component="li" paragraph>
                <strong>Targeting and Tracking Technologies</strong> - Our website may use tracking technologies such as pixels, tags, web beacons, and scripts that may collect and store information about how you interact with the site. These technologies allow us to measure the effectiveness of our content, tailor our website experience, and deliver relevant information. These may also be used by third-party service providers who perform analytics or operational services on our behalf.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Section 3: Third-Party Cookies */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">Third-Party Cookies</Typography>
            </Box>
            <Typography paragraph>
              Some cookies on our website may be set by third parties that provide services on our behalf. These providers may use cookies or other tracking technologies to collect information about your browsing activities across different websites or platforms.
            </Typography>
            <Typography paragraph>
              These third-party cookies are subject to the privacy policies of those external service providers. We recommend reviewing their policies to understand how your data may be processed.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 4: How You Can Manage Cookies */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">How You Can Manage Cookies</Typography>
            </Box>
            <Typography paragraph>
              You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by adjusting your browser settings. Most browsers allow you to:
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, mb: 1.5 }}>
              <Typography component="li" paragraph>
                See what cookies are stored
              </Typography>
              <Typography component="li" paragraph>
                Block cookies from specific websites
              </Typography>
              <Typography component="li" paragraph>
                Delete all cookies upon closing the browser
              </Typography>
              <Typography component="li" paragraph>
                Notify you before cookies are stored
              </Typography>
            </Box>
            <Typography paragraph>
              Please note that if you choose to disable or block cookies, certain parts of our website may not function properly.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 5: Changes to This Policy */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">Changes to This Policy</Typography>
            </Box>
            <Typography paragraph>
              We may update this Cookie Policy from time to time in order to reflect changes in law, our technology, or our practices. We encourage you to periodically review this page for the latest information. Continued use of our website after changes are made indicates your acceptance of the updated policy.
            </Typography>
          </CardContent>
        </Card>

        {/* Section 6: Contact Us */}
        <Card variant="outlined" sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="h6">Contact Us</Typography>
            </Box>
            <Typography paragraph>
              If you have any questions or concerns about this Cookie Policy or our use of cookies and tracking technologies, please contact us at: <Link href="mailto:management@anypaperpro.com">management@anypaperpro.com</Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
      <Footer />
    </>
  );
};